import React, { useContext, useMemo } from 'react';
import classNames from 'classnames';
import { useSurveyTheme } from '@sm/webassets';
import { L10NContext } from '@sm/intl';
import { getThemeLayoutType } from '~app/helpers/themeLayout';
import useStyles from './useStyles';

export type LayoutContainerName =
  /** Outer/Wrapping container */
  | 'layoutContainer'
  /**  Left/Right/Top Spacing/Image container */
  | 'partialContainer'
  /** Outer Content container */
  | 'contentContainer'
  /**  */
  | 'topContainer'
  /** Inner Content Container */
  | 'mainContainer'
  /** Logo container (moves between Partial <=> Content) */
  | 'logoContainer'
  /** Toolbar container (Exit & Locale, etc) */
  | 'toolbarContainer';

/** Allows a string (ID) or any HTMLDivElement props with React.Ref */
export type ContainerProps = React.HTMLAttributes<HTMLDivElement> & { ref?: React.RefObject<HTMLDivElement> };

export type LayoutContainerOptions = Record<LayoutContainerName, ContainerProps>;

export type Props = React.PropsWithChildren<{
  /** An element to show in the logo container */
  logo?: React.ReactElement | false;
  /** Element(s) to show in the toolbar container */
  tools?: React.ReactElement | false;
  /** Container Options (props) */
  options: Partial<LayoutContainerOptions>;
}>;

const defaultContainerProps: LayoutContainerOptions = {
  layoutContainer: { id: 'survey-layout-container' },
  partialContainer: { id: 'survey-layout-partial-container' },
  contentContainer: { id: 'survey-layout-content-container' },
  topContainer: { id: 'survey-layout-top-container' },
  mainContainer: { id: 'survey-main-content' },
  logoContainer: { id: 'survey-logo-container' },
  toolbarContainer: { id: 'survey-toolbar-container' },
};

function Layout({ options: containerOptions = {}, logo: Logo, tools: Tools, children }: Props): React.ReactElement {
  const { layout: layoutName } = useSurveyTheme();
  const { isBanner } = getThemeLayoutType(layoutName);
  const { isRTL } = useContext(L10NContext);

  const isToolbarEnabled = !!Tools;
  const isLogoEnabled = !!Logo;

  const {
    layoutContainer,
    layoutLogoContainer,
    layoutPartialContainer,
    layoutToolbarContainer,
    layoutContentContainer,
    layoutTopContainer,
    layoutMainContainer,
  } = useStyles({ isRTL });

  const {
    layoutContainer: { className: layoutContainerClasses, ...layoutContainerProps },
    partialContainer: { className: partialContainerClasses, ...partialContainerProps },
    contentContainer: { className: contentContainerClasses, ...contentContainerProps },
    topContainer: { className: topContainerClasses, ...topContainerProps },
    mainContainer: { className: mainContainerClasses, ...mainContainerProps },
    toolbarContainer: { className: toolbarContainerClasses, ...toolbarContainerProps },
    logoContainer: { className: logoContainerClasses, ...logoContainerProps },
  } = useMemo(() => {
    return Object.keys(containerOptions).reduce((result: LayoutContainerOptions, key: string) => {
      return { ...result, [key]: containerOptions[key as LayoutContainerName] || {} };
    }, defaultContainerProps);
  }, [containerOptions]);

  return (
    <div className={classNames(layoutContainer, layoutContainerClasses)} {...layoutContainerProps}>
      <div className={classNames(layoutPartialContainer, partialContainerClasses)} {...partialContainerProps}>
        <If condition={isBanner && isLogoEnabled}>
          <div className={classNames(layoutLogoContainer, logoContainerClasses)} {...logoContainerProps}>
            {Logo}
          </div>
        </If>
      </div>
      <div className={classNames(layoutContentContainer, contentContainerClasses)} {...contentContainerProps}>
        <div className={classNames(layoutTopContainer, topContainerClasses)} {...topContainerProps}>
          <If condition={isToolbarEnabled}>
            <div className={classNames(layoutToolbarContainer, toolbarContainerClasses)} {...toolbarContainerProps}>
              {Tools}
            </div>
          </If>
          <If condition={!isBanner && isLogoEnabled}>
            <div className={classNames(layoutLogoContainer, logoContainerClasses)} {...logoContainerProps}>
              {Logo}
            </div>
          </If>
        </div>
        <div className={classNames(layoutMainContainer, mainContainerClasses)} {...mainContainerProps}>
          {children}
        </div>
      </div>
    </div>
  );
}

export default Layout;
